import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { usePhoneField } from '@/hooks/usePhoneField'
import { CacheKeys } from '@/lib/cache'
import { addOrgMember, type OrgMemberDetails } from '@/models/user'
import { NEW_ORG_MEMBER, OrgMemberFields } from './OrgMemberFields'

export function AddOrgMemberDialog({
  orgId,
  orgName,
  children,
}: {
  orgId: string
  orgName?: string
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [details, setDetails] = useState<OrgMemberDetails>(NEW_ORG_MEMBER)
  const queryClient = useQueryClient()
  const phoneField = usePhoneField()

  function reset() {
    setDetails(NEW_ORG_MEMBER)
    phoneField.clear()
  }

  const mutation = useMutation({
    mutationFn: () => addOrgMember(orgId, details),
    onSuccess: () => {
      toast.success('User added to organization')
      queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS, orgId] })
      reset()
      setOpen(false)
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to add user'
      toast.error(message)
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!phoneField.check(details.mobile)) return
    mutation.mutate()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value)
        if (!value) reset()
      }}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add user to organization</DialogTitle>
            <DialogDescription>
              Add a user directly to {orgName ? <strong>{orgName}</strong> : 'this organization'}. They become
              an active member immediately.
            </DialogDescription>
          </DialogHeader>
          <OrgMemberFields
            idPrefix="add-member"
            details={details}
            onChange={setDetails}
            phoneError={phoneField.error}
          />
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending || !details.firstName || !details.lastName || !details.email}
              loading={mutation.isPending ? 'Adding...' : undefined}
            >
              Add user
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
